/**
 * ARIA Redux Store
 * Version: 1.0.0
 */

import { configureStore, createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { TypedUseSelectorHook } from 'react-redux';
import { useDispatch, useSelector } from 'react-redux';
import type { JobStatus, IdentifierType } from '../types';

// UI State slice - tracks active tab, filters, modals
interface UIState {
  dashboardActiveTab: number; // ARIA-1: Dashboard tabs
  jobsFilter: {
    status?: JobStatus;
    identifierType?: IdentifierType; // ARIA-7
    search?: string;
    excludeToday?: boolean; // ARIA-4
    excludeEstimates?: boolean; // ARIA-5
  };
  sidebarCollapsed: boolean;
  activeModal: string | null;
  selectedJobId: string | null;
}

const initialUIState: UIState = {
  dashboardActiveTab: 0,
  jobsFilter: {},
  sidebarCollapsed: false,
  activeModal: null,
  selectedJobId: null,
};

const uiSlice = createSlice({
  name: 'ui',
  initialState: initialUIState,
  reducers: {
    setDashboardTab: (state, action: PayloadAction<number>) => {
      state.dashboardActiveTab = action.payload;
    },
    setJobsFilter: (state, action: PayloadAction<Partial<UIState['jobsFilter']>>) => {
      state.jobsFilter = { ...state.jobsFilter, ...action.payload };
    },
    clearJobsFilter: (state) => {
      state.jobsFilter = {};
    },
    toggleSidebar: (state) => {
      state.sidebarCollapsed = !state.sidebarCollapsed;
    },
    openModal: (state, action: PayloadAction<string>) => {
      state.activeModal = action.payload;
    },
    closeModal: (state) => {
      state.activeModal = null;
    },
    selectJob: (state, action: PayloadAction<string | null>) => {
      state.selectedJobId = action.payload;
    },
  },
});

// User state slice
interface UserState {
  isAuthenticated: boolean;
  user: {
    id: string;
    email: string;
    name: string;
    roles: string[];
  } | null;
  // Set when logout is triggered by session expiry or an idle timeout (not a
  // manual sign-out), so the Login page can explain why the user landed back
  // there. Read once and cleared — see clearSessionMessage.
  sessionMessage: string | null;
}

const initialUserState: UserState = {
  isAuthenticated: false,
  user: null,
  sessionMessage: null,
};

const userSlice = createSlice({
  name: 'user',
  initialState: initialUserState,
  reducers: {
    setUser: (state, action: PayloadAction<UserState['user']>) => {
      state.isAuthenticated = !!action.payload;
      state.user = action.payload;
    },
    // action.payload, if provided, is shown once on the Login page. Manual
    // sign-out (Sidebar) passes nothing, so no message appears.
    logout: (state, action: PayloadAction<string | undefined>) => {
      state.isAuthenticated = false;
      state.user = null;
      state.sessionMessage = action.payload ?? null;
    },
    clearSessionMessage: (state) => {
      state.sessionMessage = null;
    },
  },
});

// Notifications state slice
interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  timestamp: number;
}

interface NotificationsState {
  items: Notification[];
}

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState: { items: [] } as NotificationsState,
  reducers: {
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>) => {
      state.items.push({
        ...action.payload,
        id: Math.random().toString(36).substr(2, 9),
        timestamp: Date.now(),
      });
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((n) => n.id !== action.payload);
    },
    clearNotifications: (state) => {
      state.items = [];
    },
  },
});

// Export actions
export const { setDashboardTab, setJobsFilter, clearJobsFilter, toggleSidebar, openModal, closeModal, selectJob } =
  uiSlice.actions;
export const { setUser, logout, clearSessionMessage } = userSlice.actions;
export const { addNotification, removeNotification, clearNotifications } = notificationsSlice.actions;

// Configure store
export const store = configureStore({
  reducer: {
    ui: uiSlice.reducer,
    user: userSlice.reducer,
    notifications: notificationsSlice.reducer,
  },
});

// Export types
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Typed hooks
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
